// ── Zenmetry App Entry Point ──

import { toggleLang } from './i18n';
import './components/register';
import './toast';
import { tryRestoreSession, setLogoutFn } from './api';
import { currentUser } from './state';
import {
  clearSearch, toggleSearch, toggleUserMenu, closeUserMenu,
  setDeadlineFilter, setStatusFilter, toggleFilterExpand, setSearchQuery,
} from './filters';
import {
  switchAuthTab, handleLogin, handleSignup, handleResetPassword,
  handleUpdatePassword, enterApp, logout, leaveSettingsWhenSignedOut,
  updatePasswordHints, renderAgreeTermsText, handlePasswordResetHash, handleConfirmEmailHash,
  validateSignupForm, validateResetForm, validateUpdatePasswordForm,
  renderAuthPanel, setAuthInviteNotice,
} from './auth';
import {
  saveProfile, updateProfileField,
  submitOnboarding,
  finishOnboarding,
} from './profile';
import {
  switchMainTab, switchInputMode, handleTextAnalyze, openCreateFab,
  navigateToGoal, initSettingsHashListener, openInviteAcceptFromHash,
} from './navigation';
import {
  handleFileSelect, removeFile, cancelAnalysis,
  retrySubtaskGeneration,
  renderUploadList,
  loadAllGoals,
  loadMoreDoneGoals,
} from './upload';
import { renderGoalViews, toggleEventDone, toggleEventsDoneSection } from './goals';
import './effects';
import {
  toggleGoalDone,
  toggleSubtask, declineReviewCloseProposal, deleteSubtaskUI, resetDecisionUI,
  addSubtask, askAI, makeDecision, selectCanonicalJudgmentOption, holdCanonicalJudgmentChoice, submitFreeDecision, answerGoalActor,
  editGoalDeadline,
  toggleSubtaskDesc, editSubtaskTitle, editGoalTitle, editGoalDod, editSubtaskDeadline,
  deleteGoalUI,
  approveAiResultUI, toggleRejectFeedback, rejectAiResultUI, answerReviewProposal,
  toggleAiResult, copyAiResult,
  cycleExecutor,
  openLightbox,
  dismissHold,
} from './goals-actions';
import { renderSearch } from './filters';
import { handleWorkListAction } from './work-list-actions';
import type { WorkListAction } from './components/work-list';
import type { AppRailAction } from './components/app-rail';
import type { ActorJudgmentAnswerDetail } from './components/actor-judgment-panel';
import { isSettingsHash } from './settings-nav-logic';
import { classifyInviteAcceptHash } from './invite-accept-logic';
import type {
  FilterBarActionDetail,
  GoalComponentActionDetail,
  SearchBoxActionDetail,
  SubtaskComponentActionDetail,
  UploadFileActionDetail,
} from './components/shared';

function switchTab(tab: string) {
  switchMainTab(tab);
}

function showCreateModal(mode: string) {
  switchMainTab('create');
  switchInputMode(mode);
}

function isHTMLElement(target: EventTarget | null): target is HTMLElement {
  return target instanceof HTMLElement;
}

function bindClick(selector: string, handler: (event: Event, element: HTMLElement) => void) {
  document.querySelectorAll<HTMLElement>(selector).forEach((element) => {
    element.addEventListener('click', (event) => handler(event, element));
  });
}

function bindStaticEventHandlers() {
  bindClick('header h1', () => {
    if (location.hash === '#settings' || location.hash.startsWith('#settings-')) {
      history.pushState('', document.title, location.pathname + location.search);
      window.dispatchEvent(new Event('hashchange'));
    }
    switchMainTab('today');
  });
  bindClick('.user-menu-wrap > .header-icon-btn', () => toggleUserMenu());
  bindClick('#userDropdownSettingsLink', () => closeUserMenu());
  bindClick('#logoutMenuBtn', () => logout());
  bindClick('#onboardingSubmitBtn', () => submitOnboarding());
  bindClick('#onboardingFinishBtn', () => finishOnboarding());
  bindClick('#langToggleBtn', () => toggleLang());
  bindClick('.main-tab:nth-of-type(1)', () => switchMainTab('today'));
  bindClick('.main-tab:nth-of-type(2)', () => switchMainTab('all'));
  bindClick('.main-tab:nth-of-type(3)', () => switchMainTab('done'));
  bindClick('.main-tab:nth-of-type(4)', () => switchMainTab('create'));
  bindClick('.input-mode-btn:nth-of-type(1)', () => switchInputMode('screenshot'));
  bindClick('.input-mode-btn:nth-of-type(2)', () => switchInputMode('text'));
  bindClick('#textAnalyzeBtn', () => handleTextAnalyze());
  bindClick('#createFab', () => openCreateFab());
  bindClick('[data-empty-action="sync"]', () => {
    switchTab('done');
    document.getElementById('emptySyncCard')?.click();
  });
  bindClick('[data-empty-action="screenshot"]', () => showCreateModal('screenshot'));
  bindClick('[data-empty-action="text"]', () => showCreateModal('text'));
  bindClick('[data-create-mode="screenshot"]', () => showCreateModal('screenshot'));
  bindClick('[data-create-mode="text"]', () => showCreateModal('text'));
  bindClick('#dropZone', () => {
    (document.getElementById('fileInput') as HTMLInputElement | null)?.click();
  });

  const fileInput = document.getElementById('fileInput') as HTMLInputElement | null;
  fileInput?.addEventListener('change', (event) => handleFileSelect(event));
}

window.renderAgreeTermsText = renderAgreeTermsText;
window.updatePasswordHints = updatePasswordHints;

function bindDelegatedEventHandlers() {
  document.addEventListener('workspace-menu-selected', () => closeUserMenu());
  document.addEventListener('click', (event) => {
    if (!isHTMLElement(event.target)) return;
    const target = event.target;

    // Let native anchor navigation win for external links in legacy goal cards.
    const anchor = target.closest<HTMLAnchorElement>('a[href]');
    if (anchor) return;

    const navigateBtn = target.closest<HTMLElement>('[data-navigate-goal-id]');
    if (navigateBtn) {
      void navigateToGoal(navigateBtn.dataset.navigateGoalId || '');
      return;
    }
    const eventDoneBtn = target.closest<HTMLElement>('[data-event-done-id]');
    if (eventDoneBtn) {
      void toggleEventDone(eventDoneBtn.dataset.eventDoneId || '');
      return;
    }

    const eventsDoneToggle = target.closest<HTMLElement>('.events-done-toggle');
    if (eventsDoneToggle) {
      toggleEventsDoneSection(eventsDoneToggle);
      return;
    }

    const lightboxTarget = target.closest<HTMLElement>('[data-lightbox-src]');
    if (lightboxTarget) {
      openLightbox(lightboxTarget.dataset.lightboxSrc || '');
      return;
    }

    const dismissHoldBtn = target.closest<HTMLElement>('[data-dismiss-hold-goal-id]');
    if (dismissHoldBtn) {
      const goalId = dismissHoldBtn.dataset.dismissHoldGoalId || '';
      const eventId = dismissHoldBtn.dataset.dismissHoldEventId || '';
      const eventTitle = dismissHoldBtn.dataset.dismissHoldEventTitle;
      if (goalId && eventId) void dismissHold(goalId, eventId, eventTitle);
    }
  });
}

function bindComponentEventHandlers() {
  document.addEventListener('search-box-action', (event: Event) => {
    const detail = (event as CustomEvent<SearchBoxActionDetail>).detail;
    if (detail.action === 'toggle-search') toggleSearch();
    if (detail.action === 'clear-search') clearSearch();
    if (detail.action === 'query-change') setSearchQuery(detail.query || '');
  });

  document.addEventListener('goal-list-load-more', () => {
    void loadMoreDoneGoals();
  });

  document.addEventListener('filter-bar-action', (event: Event) => {
    const detail = (event as CustomEvent<FilterBarActionDetail>).detail;
    const fakeElement = event.target as HTMLElement;
    if (detail.action === 'deadline-filter-change') setDeadlineFilter(detail.value, fakeElement);
    if (detail.action === 'status-filter-change') setStatusFilter(detail.value, fakeElement);
  });

  document.addEventListener('upload-file-action', (event: Event) => {
    const detail = (event as CustomEvent<UploadFileActionDetail>).detail;
    if (detail.action === 'navigate-goal' && detail.goalId) void navigateToGoal(detail.goalId);
    if (detail.action === 'cancel-analysis' && detail.uploadId) cancelAnalysis(detail.uploadId);
    if (detail.action === 'remove-file' && detail.uploadId) removeFile(detail.uploadId);
  });

  document.addEventListener('profile-save', () => { void saveProfile(); });
  document.addEventListener('profile-field-change', (event: Event) => {
    const detail = (event as CustomEvent<{ key?: string; value?: string }>).detail;
    if (!detail?.key || typeof detail.value !== 'string') return;
    updateProfileField(detail.key as Parameters<typeof updateProfileField>[0], detail.value);
  });
  document.addEventListener('work-list-action', (event: Event) => {
    void handleWorkListAction((event as CustomEvent<WorkListAction>).detail);
  });
  document.addEventListener('app-rail-action', (event: Event) => {
    const detail = (event as CustomEvent<AppRailAction>).detail;
    if (detail.action === 'logout') { logout(); return; }
    // Leaving settings asks about unsaved changes first; a "stay" keeps settings open.
    if (isSettingsHash(location.hash)) {
      history.pushState('', document.title, location.pathname + location.search);
      window.dispatchEvent(new Event('hashchange'));
      if (isSettingsHash(location.hash)) return;
    }
    switchMainTab(detail.tab);
    window.scrollTo({ top: 0 });
  });

  document.addEventListener('auth-tab-login', () => switchAuthTab('login'));
  document.addEventListener('auth-tab-signup', () => switchAuthTab('signup'));
  document.addEventListener('auth-tab-reset', () => switchAuthTab('reset'));
  document.addEventListener('submit', (event: Event) => {
    const form = event.target as HTMLElement | null;
    if (!form) return;
    if (form.id === 'loginForm') void handleLogin(event);
    if (form.id === 'resetForm') void handleResetPassword(event);
    if (form.id === 'signupForm') void handleSignup(event);
    if (form.id === 'updatePasswordForm') void handleUpdatePassword(event);
  });
  document.addEventListener('input', (event: Event) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.id === 'resetEmail') validateResetForm();
    if (target.id === 'signupName' || target.id === 'signupEmail') validateSignupForm();
    if (target.id === 'signupPassword') {
      validateSignupForm();
      updatePasswordHints();
    }
    if (target.id === 'newPassword' || target.id === 'newPasswordConfirm') validateUpdatePasswordForm();
  });
  document.addEventListener('change', (event: Event) => {
    const target = event.target as HTMLElement | null;
    if (target?.id === 'agreeTerms') validateSignupForm();
  });

  document.addEventListener('goal-toggle-done', (event: Event) => {
    const detail = (event as CustomEvent<GoalComponentActionDetail>).detail;
    void toggleGoalDone(detail.goalId, detail.value === true);
  });


  document.addEventListener('goal-edit-title', (event: Event) => {
    const detail = (event as CustomEvent<GoalComponentActionDetail>).detail;
    if (detail.element) editGoalTitle(detail.goalId, detail.element);
  });

  document.addEventListener('goal-edit-dod', (event: Event) => {
    const detail = (event as CustomEvent<GoalComponentActionDetail>).detail;
    if (detail.element) editGoalDod(detail.goalId, detail.element);
  });

  document.addEventListener('goal-edit-deadline', (event: Event) => {
    const detail = (event as CustomEvent<GoalComponentActionDetail>).detail;
    if (detail.element) editGoalDeadline(detail.goalId, detail.element, detail.currentISO || '');
  });



  document.addEventListener('goal-filter-toggle', (event: Event) => {
    const detail = (event as CustomEvent<GoalComponentActionDetail>).detail;
    if (!detail.element) return;
    toggleFilterExpand(detail.goalId, detail.section || '', detail.element);
  });




  document.addEventListener('goal-retry-subtask-generation', (event: Event) => {
    const detail = (event as CustomEvent<GoalComponentActionDetail>).detail;
    if (detail.element instanceof HTMLButtonElement) {
      void retrySubtaskGeneration(detail.goalId, detail.element);
    }
  });

  document.addEventListener('goal-add-subtask', (event: Event) => {
    const detail = (event as CustomEvent<GoalComponentActionDetail>).detail;
    void addSubtask(detail.goalId, detail.element instanceof HTMLInputElement ? detail.element : null);
  });

  document.addEventListener('goal-ask-ai', (event: Event) => {
    const detail = (event as CustomEvent<GoalComponentActionDetail>).detail;
    void askAI(detail.goalId, detail.title || '');
  });

  document.addEventListener('goal-delete', (event: Event) => {
    const detail = (event as CustomEvent<GoalComponentActionDetail>).detail;
    if (detail.element instanceof HTMLButtonElement) {
      deleteGoalUI(detail.goalId, detail.title || '', detail.element);
    }
  });


  document.addEventListener('subtask-toggle-done', (event: Event) => {
    const detail = (event as CustomEvent<SubtaskComponentActionDetail>).detail;
    void toggleSubtask(detail.subtaskId, detail.value === true);
  });

  document.addEventListener('subtask-decline-review-close', (event: Event) => {
    const detail = (event as CustomEvent<SubtaskComponentActionDetail>).detail;
    void declineReviewCloseProposal(detail.subtaskId);
  });

  document.addEventListener('subtask-toggle-desc', (event: Event) => {
    const detail = (event as CustomEvent<SubtaskComponentActionDetail>).detail;
    if (detail.element) toggleSubtaskDesc(detail.subtaskId, detail.element);
  });

  document.addEventListener('subtask-edit-title', (event: Event) => {
    const detail = (event as CustomEvent<SubtaskComponentActionDetail>).detail;
    if (detail.element) editSubtaskTitle(detail.subtaskId, detail.element);
  });

  document.addEventListener('subtask-edit-deadline', (event: Event) => {
    const detail = (event as CustomEvent<SubtaskComponentActionDetail>).detail;
    if (detail.element) editSubtaskDeadline(detail.subtaskId, detail.element, detail.currentISO || '');
  });

  document.addEventListener('subtask-delete', (event: Event) => {
    const detail = (event as CustomEvent<SubtaskComponentActionDetail>).detail;
    void deleteSubtaskUI(detail.subtaskId, detail.title || '');
  });

  document.addEventListener('subtask-reset-decision', (event: Event) => {
    const detail = (event as CustomEvent<SubtaskComponentActionDetail>).detail;
    void resetDecisionUI(detail.subtaskId);
  });

  document.addEventListener('subtask-make-decision', (event: Event) => {
    const detail = (event as CustomEvent<SubtaskComponentActionDetail>).detail;
    void makeDecision(detail.subtaskId, String(detail.value || ''), detail.label || String(detail.value || ''));
  });

  document.addEventListener('canonical-judgment-select', (event: Event) => {
    const detail = (event as CustomEvent<{
      goalId?: string;
      optionKey?: string;
      optionValue?: string;
      selectionSnapshotToken?: string;
      conditionRef?: string;
      axis?: string;
      text?: string;
    }>).detail;
    if (!detail?.goalId || !detail.optionKey || !detail.optionValue || !detail.selectionSnapshotToken) return;
    void selectCanonicalJudgmentOption(
      detail.goalId,
      detail.optionKey,
      detail.optionValue,
      detail.selectionSnapshotToken,
      detail.conditionRef && detail.axis ? { condition_ref: detail.conditionRef, axis: detail.axis } : undefined,
      detail.text,
    );
  });

  document.addEventListener('canonical-judgment-hold', (event: Event) => {
    const detail = (event as CustomEvent<{
      goalId?: string;
      selectionSnapshotToken?: string;
      conditionRef?: string;
      axis?: string;
    }>).detail;
    if (!detail?.goalId || !detail.selectionSnapshotToken) return;
    void holdCanonicalJudgmentChoice(
      detail.goalId,
      detail.selectionSnapshotToken,
      detail.conditionRef && detail.axis ? { condition_ref: detail.conditionRef, axis: detail.axis } : undefined,
    );
  });

  // #2732: 「誰が進めますか」 from the Goal detail's judgment card.
  document.addEventListener('actor-judgment-answer', (event: Event) => {
    const detail = (event as CustomEvent<ActorJudgmentAnswerDetail>).detail;
    if (!detail?.goalId || !detail.conditionRef) return;
    void answerGoalActor(detail.goalId, detail.conditionRef, detail.actor, detail.teammateUserId);
  });

  document.addEventListener('review-proposal-answer', (event: Event) => {
    const detail = (event as CustomEvent<{ goalId?: string; proposalRef?: string; answer?: 'keep' | 'withdraw' | 'add' | 'rewrite'; conditionRef?: string; draft?: string }>).detail;
    if (!detail?.goalId || !detail.proposalRef || !detail.answer || !['keep', 'withdraw', 'add', 'rewrite'].includes(detail.answer)) return;
    void answerReviewProposal(detail.goalId, detail.proposalRef, detail.answer, detail.conditionRef, detail.draft);
  });


  document.addEventListener('subtask-submit-free-decision', (event: Event) => {
    const detail = (event as CustomEvent<SubtaskComponentActionDetail>).detail;
    void submitFreeDecision(detail.subtaskId);
  });

  document.addEventListener('subtask-cycle-executor', (event: Event) => {
    const detail = (event as CustomEvent<SubtaskComponentActionDetail>).detail;
    if (detail.element) void cycleExecutor(detail.subtaskId, detail.element, detail.event || event);
  });

  document.addEventListener('ai-result-toggle', (event: Event) => {
    const detail = (event as CustomEvent<SubtaskComponentActionDetail>).detail;
    toggleAiResult(detail.subtaskId);
  });

  document.addEventListener('ai-result-copy', (event: Event) => {
    const detail = (event as CustomEvent<SubtaskComponentActionDetail>).detail;
    if (detail.element) copyAiResult(detail.element);
  });

  document.addEventListener('ai-result-approve', (event: Event) => {
    const detail = (event as CustomEvent<SubtaskComponentActionDetail>).detail;
    void approveAiResultUI(detail.subtaskId);
  });

  document.addEventListener('ai-result-toggle-reject', (event: Event) => {
    const detail = (event as CustomEvent<SubtaskComponentActionDetail>).detail;
    toggleRejectFeedback(detail.subtaskId);
  });

  document.addEventListener('ai-result-reject', (event: Event) => {
    const detail = (event as CustomEvent<SubtaskComponentActionDetail>).detail;
    void rejectAiResultUI(detail.subtaskId);
  });

}

// ── Initialization ──

// Wire up logout so api() can redirect on 401
setLogoutFn(logout);

// Auto-restore session on page load
(async () => {
  if (await handlePasswordResetHash()) return;
  if (await handleConfirmEmailHash()) return;
  const restored = await tryRestoreSession();
  if (restored) {
    enterApp();
    return;
  }
  leaveSettingsWhenSignedOut();
  if (classifyInviteAcceptHash(window.location.hash)) setAuthInviteNotice(true);
})();

// An invitation link pasted into a tab where the app is already open changes
// only the hash, so the page does not reload and enterApp does not run again.
window.addEventListener('hashchange', () => {
  if (!currentUser) leaveSettingsWhenSignedOut();
  if (!classifyInviteAcceptHash(window.location.hash)) return;
  if (currentUser) void openInviteAcceptFromHash();
  else setAuthInviteNotice(true);
});

// Lightbox & sync card setup
document.addEventListener('DOMContentLoaded', () => {
  bindStaticEventHandlers();
  bindDelegatedEventHandlers();
  bindComponentEventHandlers();
  initSettingsHashListener();
  renderAuthPanel();
  renderSearch();
  renderUploadList();
  renderGoalViews();

  document.addEventListener('zen-lang-change', () => {
    renderAuthPanel();
    renderSearch();
    renderUploadList();
    renderGoalViews();
  });

  const lb = document.getElementById('imgLightbox');
  lb.addEventListener('click', () => { lb.style.display = 'none'; });

  const syncCard = document.getElementById('emptySyncCard');
  const syncSteps = document.getElementById('emptySyncSteps');
  if (syncCard && syncSteps) {
    syncCard.addEventListener('click', () => {
      const isOpen = syncSteps.classList.toggle('open');
      syncCard.querySelector('.action-arrow').textContent = isOpen ? 'expand_less' : 'expand_more';
    });
  }
});
